import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useState } from "react";
import { useAdminListUsers } from "@/services/api/admin/admin";
import { Table, TableCard } from "@/components/application/table/table";
import { Button } from "@/components/base/buttons/button";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { RoleButtons } from "@/features/admin/components/RoleButtons";
//
import type {
  AdminListUsersParams,
  ListUsersResponse as AdminListUsersResponse,
} from "@/types/api";

export const Route = createFileRoute("/admin/users")({
  component: UsersPage,
});

function UsersPage() {
  const [query, setQuery] = useState("");
  const [flash, setFlash] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const params: AdminListUsersParams = { Query: query, Page: 1, PageSize: 25 };
  const { data, isLoading, isFetching, error, refetch } = useAdminListUsers(params);
  const errorMessage = error instanceof Error ? error.message : undefined;

  return (
    <ProtectedRoute requiredPermissions={["Users.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout>
        <div className="mx-auto max-w-6xl p-6">
          <TableCard.Root>
            <TableCard.Header
              title="Users"
              description="View users, inspect roles, and manage assignments"
              contentTrailing={
                <div className="flex gap-2">
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email"
                    className="border-arc-base-black rounded-lg border-2 px-3 py-2"
                  />
                  <Button color="secondary" onClick={() => refetch()}>
                    Search
                  </Button>
                </div>
              }
            />

            {/* Flash message (non-shifting absolute banner) */}
            {flash && (
              <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 w-full max-w-md -translate-x-1/2 px-4">
                <div
                  className={
                    flash.type === "success"
                      ? "pointer-events-auto rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-700 shadow-lg"
                      : "pointer-events-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 shadow-lg"
                  }
                  role="status"
                  aria-live="polite"
                >
                  {flash.message}
                </div>
              </div>
            )}

            {/* Table area with non-shifting layout and loading overlay */}
            <div className="relative min-h-[340px]">
              {(isLoading || isFetching) && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                  <div className="border-t-arc-base-black h-8 w-8 animate-spin rounded-full border-2 border-gray-300" />
                </div>
              )}
              {errorMessage && (
                <div className="m-4 rounded border border-red-300 bg-red-50 p-3 text-red-800">
                  {errorMessage ?? "Failed to load users"}
                </div>
              )}

              {data && (data as AdminListUsersResponse).items && (
                <Table aria-label="Users table">
                  <Table.Header columns={[{}, {}, {}, {}] as object[]}>
                    <Table.Head label="Name" />
                    <Table.Head label="Email" />
                    <Table.Head label="Roles" />
                    <Table.Head label="Actions" />
                  </Table.Header>
                  <Table.Body>
                    {(data as AdminListUsersResponse).items!.map((u) => (
                      <Table.Row key={u.id} columns={[{}, {}, {}, {}] as object[]}>
                        <Table.Cell>
                          {u.firstName} {u.lastName}
                        </Table.Cell>
                        <Table.Cell>{u.email ?? ""}</Table.Cell>
                        <Table.Cell>{Array.isArray(u.roles) ? u.roles.join(", ") : "—"}</Table.Cell>
                        <Table.Cell>
                          <RoleButtons user={u} onChanged={() => refetch()} onFlash={setFlash} />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          </TableCard.Root>
        </div>
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
